import type { Book, Gift, IncludedItem, Review, FaqItem, RichLine, SamplePage } from '../types/content'

/** Destination for every "Get Instant Access" action on the page. */
export const checkoutUrl = 'https://engineer.impultienda.ar/'

export const images = {
  /** The collection shown on tablets. Used in the hero and the buy section. */
  heroTablets:
    'https://cdn.magicpatterns.com/uploads/5YAQYTaR4aS7ZSGuu3AJAx/mockup-six-books-final.png',
}

/** The six illustrated digital books, 20 projects and challenges each. */
export const books: Book[] = [
  {
    number: '01',
    title: 'My First Builds',
    focus: 'Starting with everyday materials',
    cover: 'https://drive.google.com/thumbnail?id=1zt2KlWqGyPWHT0kF3JTa6GYu1PPkPZcm&sz=w800',
  },
  {
    number: '02',
    title: 'Wheels & Motion',
    focus: 'Exploring movement',
    cover: 'https://drive.google.com/thumbnail?id=1l0q6Wdj47-7KjRLunOm_q40B28WrZiZY&sz=w800',
  },
  {
    number: '03',
    title: 'Bridges & Structures',
    focus: 'Building and comparing structures',
    cover: 'https://drive.google.com/thumbnail?id=1q7jgS5DX2LsiQEUHfnT3vk2xCitJvEba&sz=w800',
  },
  {
    number: '04',
    title: 'Machines & Mechanisms',
    focus: 'Discovering how parts work together',
    cover: 'https://drive.google.com/thumbnail?id=1R3VHIH1A5Uk9OEkClD-HH0U1VlYU-T3a&sz=w800',
  },
  {
    number: '05',
    title: 'Science You Can Build',
    focus: 'Observing, testing and asking questions',
    cover: 'https://drive.google.com/thumbnail?id=1IbYFXqusbIuIs-Nc-Emmhf3z0r24OA_4&sz=w800',
  },
  {
    number: '06',
    title: 'Design, Test & Invent',
    focus: 'Trying ideas and improving designs',
    cover: 'https://drive.google.com/thumbnail?id=1has8bml2Bo-eDNlf6xeFl2XHXFLswy1d&sz=w800',
  },
]

/**
 * Real pages from Book 01 — My First Builds. These are the only interior pages
 * shown publicly: two spreads of two projects, complete and uncropped. Nothing
 * here is invented, and no page links to the full PDF or the delivery portal.
 */
export const samplePages: SamplePage[] = [
  {
    project: 'Marble Maze',
    page: 'page 09',
    image: 'https://cdn.magicpatterns.com/uploads/nBTXjWASNSjo7V6Eqo9wLx/real-book-01-page-09.png',
  },
  {
    project: 'Marble Maze',
    page: 'page 10',
    image: 'https://cdn.magicpatterns.com/uploads/6LPMBAtdBf2aTZN9zMWete/real-book-01-page-10.png',
  },
  {
    project: 'Desk Organizer',
    page: 'page 12',
    image: 'https://cdn.magicpatterns.com/uploads/vWNr7UWZXUD9hkkuEvMin9/real-book-01-page-12.png',
  },
  {
    project: 'Desk Organizer',
    page: 'page 13',
    image: 'https://cdn.magicpatterns.com/uploads/tBfvsq9M6U1ttoXsUUCzvV/real-book-01-page-13.png',
  },
]

export const painPoints: RichLine[] = [
  [
    { text: 'Your child spends hours ' },
    { text: 'glued to a screen', tone: 'brick' },
    { text: ' and ' },
    { text: "you don't know what to do anymore", tone: 'navy' },
    { text: '.' },
  ],
  [
    { text: 'Every time you take the phone away, ' },
    { text: 'the meltdown begins', tone: 'brick' },
    { text: '.' },
  ],
  [
    { text: 'You worry about ' },
    { text: 'the consequences', tone: 'navy' },
    { text: ' of seeing your child ' },
    { text: 'constantly glued to a screen', tone: 'brick' },
    { text: '.' },
  ],
  [
    { text: 'You hand over the phone and then ' },
    { text: 'you regret it', tone: 'brick' },
    { text: '.' },
  ],
  [
    { text: 'You love your child deeply, but ' },
    { text: "you don't feel like the parent you'd like to be", tone: 'navy' },
    { text: '.' },
  ],
]

export const includedItems: IncludedItem[] = [
  {
    title: 'Six illustrated digital books',
    detail:
      'with 20 projects and challenges each — 120 in total, ordered from first builds to designing and inventing.',
  },
  {
    title: 'Illustrated step-by-step pages',
    detail:
      'with materials lists and numbered illustrations to follow as you build.',
  },
  {
    title: 'Everyday materials',
    detail:
      'like cardboard, tubes, straws, tape and marbles. Each project lists what it needs before you start.',
  },
  {
    title: 'A challenge at the end of every project',
    detail:
      'inviting your child to change one thing and test the build again, so the thinking continues after it works.',
  },
  {
    title: 'Read on a tablet or print at home',
    detail: 'every page is laid out for both, so you can build from a screen or from paper.',
  },
  {
    title: 'Instant, lifetime digital access',
    detail: 'with no subscription and no expiration date.',
  },
]

export const outcomes: RichLine[] = [
  [
    { text: "You'll turn chaotic afternoons into " },
    { text: 'moments of play, joy and connection', tone: 'forest' },
    { text: ' — with less yelling and frustration.' },
  ],
  [
    { text: 'Your child will start ' },
    { text: 'building things with their own hands', tone: 'navy' },
    { text: ' instead of immediately reaching for the phone.' },
  ],
  [
    { text: "You'll be able to " },
    { text: 'set healthier screen limits', tone: 'forest' },
    { text: ' without constant shouting, punishment or guilt afterwards.' },
  ],
  [
    { text: 'Your child will stop reaching for the phone every time boredom hits… and start ' },
    { text: 'creating out of curiosity and play', tone: 'navy' },
    { text: '.' },
  ],
  [
    { text: "You'll discover you can parent with " },
    { text: 'fewer screens, less guilt and more creativity', tone: 'forest' },
    { text: '.' },
  ],
]

/**
 * Bonus content. Each gift is an independent entry — edit the label, title or
 * image of any single bonus here without touching the rest of the section.
 *
 * These eight are the exact eight delivered inside The Workshop portal, in the
 * same order. If one changes here it has to change there too, or a buyer opens
 * the portal and cannot find something the landing promised.
 *
 * The images are the illustrated bonus COVER artworks, not interior samples.
 *
 * The papercraft megapack and the calligraphy programme are deliberately NOT
 * here: they are the paid Creator Pack (order bump at checkout, locked card in
 * the portal). They must not appear anywhere on this page as included.
 */
export const gifts: Gift[] = [
  {
    label: 'Bonus 1',
    title: '130 Cognitive Flashcards',
    subtitle: 'Emotions, animals, the alphabet and first words, ready to print',
    image: 'https://cdn.magicpatterns.com/uploads/wzH3jtsiP3c3rbGNkDkUZG/bonus-01.png',
  },
  {
    label: 'Bonus 2',
    title: "The Little Chef's Math Menu",
    subtitle: 'Counting, money and problem-solving hidden inside cooking play',
    image: 'https://cdn.magicpatterns.com/uploads/3t1JSN3xLc6UXxEjksNz4R/bonus-02.png',
  },
  {
    label: 'Bonus 3',
    title: 'The 3D Farm & Wearable Watch',
    subtitle: 'A paper farm to build and a watch your child can really wear',
    image: 'https://cdn.magicpatterns.com/uploads/uVrPZhnuswvBBNcqZoDnCT/bonus-03.png',
  },
  {
    label: 'Bonus 4',
    title: '360° Brain Stimulation Program',
    subtitle: 'Mazes, matching grids and pattern work, ordered by stage',
    image: 'https://cdn.magicpatterns.com/uploads/iqkmsfmz3erpVW14yo6gG1/bonus-04.png',
  },
  {
    label: 'Bonus 5',
    title: 'Early Genius Development Playkit',
    subtitle: 'Sorting, pairing and odd-one-out for the youngest builders',
    image: 'https://cdn.magicpatterns.com/uploads/bVHAMTPeAifRpVJsfjAX1S/bonus-05.png',
  },
  {
    label: 'Bonus 6',
    title: 'My Very First Speech & Language Book',
    subtitle: 'An illustrated guide to first words, sounds and storytelling',
    image: 'https://cdn.magicpatterns.com/uploads/fjFXvySqagACreJwF7VvpR/bonus-06.png',
  },
  {
    label: 'Bonus 7',
    title: 'Early Speech & Vocabulary Starter',
    subtitle: 'Short prompts for everyday conversational play',
    image: 'https://cdn.magicpatterns.com/uploads/gvBs7vmaTRcTV484FovXYv/bonus-07.png',
  },
  {
    label: 'Bonus 8',
    title: 'Family Bible Activities & Values',
    subtitle: 'Illustrated retellings and value-focused worksheets',
    image: 'https://cdn.magicpatterns.com/uploads/jNfSPssLGha3pfesYB4Pk2/bonus-08.png',
  },
]

export const reviews: Review[] = [
  {
    name: 'Emily',
    country: 'Colombia',
    rating: 5,
    text: 'I thought it would be complicated to put something together myself, but the guides are crystal clear. My son built his first robot with things we already had in the kitchen.',
  },
  {
    name: 'Madison',
    country: 'Argentina',
    rating: 5,
    text: "The best part is that I didn't have to buy anything special. Cardboard, bottle caps, and done. Forty minutes of complete silence while I work.",
  },
  {
    name: 'Olivia',
    country: 'Mexico',
    rating: 4,
    text: "Very good, although some projects took me longer than the guide said. Still worth it, my daughter didn't put the project down all afternoon.",
  },
  {
    name: 'Hannah',
    country: 'Chile',
    rating: 5,
    text: 'At first I hesitated because I thought I would have to explain everything myself, but the steps are so clear that my kids follow them alone and build everything with their hands.',
  },
  {
    name: 'Ashley',
    country: 'Peru',
    rating: 5,
    text: 'We print one project a week and it has become our Sunday plan. My two kids fight over who gets to cut the cardboard.',
  },
  {
    name: 'Jessica',
    country: 'Ecuador',
    rating: 5,
    text: 'I bought it to try it out and it ended up being what we use the most at home this month. You can tell it was designed by people who understand kids.',
  },
]

export const faqs: FaqItem[] = [
  {
    question: 'Is it physical or digital?',
    answer:
      'It is 100% digital. After your purchase you receive an email with immediate access to the six books and every printable activity. No physical books, devices or materials are shipped.',
  },
  {
    question: 'What ages is the new collection for?',
    answer:
      'The six books are written for ages 6 to 12, with adult support where a project involves cutting or small parts — each page says when a grown-up is needed. Some of the original extras included with your purchase also work well for younger children.',
  },
  {
    question: 'Do I need special materials?',
    answer:
      'Most projects use cardboard, tubes, straws, tape, pencils and similar everyday items. Every project opens with its materials list, so you can check what you have before you start and pick up anything missing.',
  },
  {
    question: 'Do I need a printer?',
    answer:
      'No. Every page is laid out to be read on a tablet, phone or computer, and printed at home if you prefer building from paper. Some of the bonus activities are designed to be printed.',
  },
  {
    question: "What if I don't have time?",
    answer:
      'Most projects take between 10 and 30 minutes. You can start with a single 10-minute activity a day and still see the difference in your afternoons.',
  },
  {
    question: "What if it doesn't work for me?",
    answer:
      'You have our 7-day Hands-On Guarantee. Try the projects, and if it is not for your family, write to us and we refund you, no awkward questions.',
  },
  {
    question: 'Does the access expire?',
    answer:
      'Never. Your access is lifetime and includes every future update to the collection at no extra cost.',
  },
  {
    question: 'Is it safe to pay here?',
    answer:
      'Yes. Payments are processed through an encrypted checkout, and we never store your card details.',
  },
]
